import { removeUndefinedProperties } from '@/Infrastructure/Utilities/object.type';
import { BaseApiRequest, BasePutApiRequest, LegType, ParsedDate, parseIsoDate } from '@api/Trinium/types';
import { chargesAndPaymentsSlice } from './slice';
import { store } from '@reduxStore';
import { consignmentSlice } from '../Consignment/slice';

/**
 * Raw object returned from the Charges API
 */
export interface ChargeRaw {
  id: number;
  svcId: string;
  custId: string;
  amt?: number | null;
  corpAmt?: number;
  agentAmt?: number;
  insAmt?: number;
  embedGrp?: string;
  invSts: string;
  invType?: string;
  invNo?: number;
  accNo?: number;
  vendorClgId?: number;
  notes?: string;
  intNotes?: string;
  sysMsgs?: string;
  isOnHold?: boolean;
  createdUsrId: string;
  createdUsrName: string;
  createdDate?: string;
  cust: {
    name: string;
    availCredit?: number;
    creditLmt?: number;
  };
  inv?: {
    type?: string;
    date: string;
    isPrinted: boolean;
    isPosted: boolean;
    paySts: 'F' | 'P' | undefined;
  };
  svc: ChargeService;
}

export interface ChargeService {
  name: string;
  isAcc: boolean;
  isPrimary: boolean;
  isActive?: boolean;
}

/**
 * Excluded fields from the Raw Response that are replaced with parse methods to create better typed schema
 */
type ExcludedChargeRawFields = 'amt' | 'createdDate' | 'inv';

/**
 * Strongly typed Charge object used throughout the application
 */
export type Charge = Omit<ChargeRaw, ExcludedChargeRawFields> & {
  createdDateTime?: ParsedDate;
  amount?: number | null;
  /** Fuel surcharge percent of its dray, calculated by the Charges grid; never sent to or read from the API */
  pct?: number;
  invoice: {
    date?: ParsedDate;
    isPrinted: boolean;
    isPosted: boolean;
    paidStatus: 'F' | 'P' | undefined;
  };
};

export function transformChargeResponse(charges: ChargeRaw[]) {
  return charges.map(
    ({ amt, createdDate, inv, ...persistedChargeFields }) =>
      ({
        amount: amt,
        ...persistedChargeFields,
        createdDateTime: parseIsoDate(createdDate),
        invoice: {
          ...inv,
          paidStatus: inv?.paySts,
          date: parseIsoDate(inv?.date)
        }
      }) as Charge
  );
}

/** Raw API Mutable Object to be sent up  */
export type MutableCharge = Pick<ChargeRaw, 'id'> & Partial<ChargeRaw>;

export function toMutableApiCharge({ amount, ...persistedFields }: Charge): MutableCharge {
  return removeUndefinedProperties({
    ...persistedFields,
    amt: amount ?? undefined
  });
}

/**
 * An invoiced charge is locked on the server. Shared by the toolbar row actions and the mobile card
 * actions so the desktop and mobile paths cannot disagree about when Delete is offered.
 */
export function canDeleteCharge(charge: Charge) {
  return charge.invSts !== 'I';
}

interface PaymentNotesRaw {
  usrName: string;
  usrId: string;
  date: string;
  type: string;
  note: string;
}

export type PaymentNoteTypes = 'Audit' | 'Private' | 'Public';

/**
 * Driver Payment Notes
 */
export type PaymentNotes = Omit<PaymentNotesRaw, 'date' | 'type'> & {
  id: number;
  date: ParsedDate;
  type: PaymentNoteTypes;
  userId: string;
  userName: string;
};

/**
 * Raw API response schema to the /payments fetch request
 */
export interface PaymentRaw {
  id: number;
  amt: number;
  aprvdUsrId: string;
  aprvdUsrName?: string;
  /** Payment service date (costleg.clg_to_date), the date the service was performed. YYYY-MM-DD */
  date?: string;
  aprvdDate: string;
  createdUsrId: string;
  createdUsrName: string;
  createdDate: string;
  authNo: number;
  /** Flag indicating that the Pre-Release / Un-Release button should be enabled */
  canRelease?: boolean;
  /** Flag indicating that the Payment can be modified */
  canUpdate?: boolean;
  /** Flag indicating that the Payment can be deleted */
  canDelete?: boolean;
  isOnHold?: boolean;
  supId?: string;
  puId?: string;
  delvId?: string;
  sup: {
    name: string;
    type: string;
  };
  delv?: {
    name: string;
    city: string;
    state: string;
  };
  csnId?: number;
  legId?: number;
  leg?: {
    no: number;
    type: LegType;
  };
  payInvNo?: number;
  payInv?: {
    date: string;
    chkNo: string;
    chkDate: string;
  };
  payeeId: string;
  payee: {
    name: string;
    type: string;
  };
  pu?: {
    name: string;
    city: string;
    state: string;
  };
  svcId: string;
  svc: {
    name: string;
    isAcc: boolean;
    isPrimary: boolean;
    isActive?: boolean;
  };
  notes?: PaymentNotesRaw[];
}

type ExcludedPaymentRawFields = 'createdDate' | 'aprvdDate' | 'payInv' | 'payee' | 'sup' | 'notes';

export type Payment = Omit<PaymentRaw, ExcludedPaymentRawFields> & {
  serviceDate?: ParsedDate;
  createdDateTime?: ParsedDate;
  approvedDateTime?: ParsedDate;
  payInvoice?: {
    date?: ParsedDate;
    chkNo: string;
    chkDate?: ParsedDate;
  };
  payeeParsed: {
    name: string;
    type: SupplierType;
  };
  supplier: {
    name: string;
    type: SupplierType;
  };
  paymentNotes: PaymentNotes[];
  /** Payee percent calculated by the Payments grid from the linked charges; the API's stored pct is never read */
  pct?: number;
  /** Aggregation rows that are calculated when generating the bottom pinned row of the Payments grid */
  agentTotal?: number;
  grossProfit?: number;
  drayProfit?: number;
  /** ----------------- */
};

interface SupplierType {
  group: 'Agent' | 'Corporate' | 'Driver' | 'Salesman' | 'Misc';
  code: string;
}

function parseSupplierType(payeeTypeRaw: string): SupplierType {
  switch (payeeTypeRaw.toLowerCase()) {
    case 'agent':
      return {
        group: 'Agent',
        code: payeeTypeRaw
      };
    case 'ap vendor':
    case 'broker carrier':
    case 'owner driver':
    case 'permanent':
    case 'trip lease':
      return {
        group: 'Driver',
        code: payeeTypeRaw
      };
    case 'corporate':
      return {
        group: 'Corporate',
        code: payeeTypeRaw
      };
    case 'sales':
      return {
        group: 'Salesman',
        code: payeeTypeRaw
      };
    case 'chassis provider':
    case 'steamship line':
      return {
        group: 'Misc',
        code: payeeTypeRaw
      };
    default:
      throw new Error(`Unknown Payee Type could not be parsed: ${payeeTypeRaw}`);
  }
}

export function transformPayment({ payInvNo, createdDate, aprvdDate, payInv, payee, sup, notes, ...persistedPaymentFields }: PaymentRaw) {
  return {
    ...persistedPaymentFields,
    serviceDate: parseIsoDate(persistedPaymentFields.date),
    createdDateTime: parseIsoDate(createdDate),
    approvedDateTime: parseIsoDate(aprvdDate),
    // Explicitly set this because if it is not included on the API response, such as performing an Un-Release action,
    // than the Object.Assign cache update does not actually change the value aka remove the value as we want/expect
    payInvNo: payInvNo ?? undefined,
    payInvoice: {
      date: parseIsoDate(payInv?.date),
      chkNo: payInv?.chkNo,
      chkDate: parseIsoDate(payInv?.chkDate)
    },
    payeeParsed: {
      name: payee.name,
      type: parseSupplierType(payee.type)
    },
    supplier: {
      name: sup.name,
      type: parseSupplierType(sup.type)
    },
    paymentNotes: notes?.map(({ date, usrId, usrName, ...basefields }, index) => ({
      ...basefields,
      id: index,
      userId: usrId,
      userName: usrName,
      date: parseIsoDate(date)
    }))
  } as Payment;
}

export function transformPaymentResponse(paymentsRaw: PaymentRaw[]) {
  return paymentsRaw.map((p) => transformPayment(p));
}

/** Immutable embedded objects on the Payment that need to be omitted */
type ImmutablePaymentField = 'sup' | 'payInv' | 'payee';

/** Raw API Mutable Object to be sent up  */
export type MutablePayment = Pick<PaymentRaw, 'id'> & Omit<Partial<PaymentRaw>, ImmutablePaymentField>;

export interface BaseChargeRequest extends BaseApiRequest {
  /** Consignment Identifier required to manually update the charges and payments fetch endpoints */
  csnId: number;
}

export interface PatchChargeRequest extends BaseChargeRequest {
  /** Charge identifier and only the mutated properties */
  charge: Partial<Pick<Charge, 'id'>>;
  optimistic?: boolean;
}

export interface DeleteChargeRequest extends BaseChargeRequest {
  /** Charge or Payment resource ID */
  id: number;
}

export interface AddChargePutRequest extends BaseChargeRequest {
  /** Charge identifier and only the mutated properties */
  charge: Partial<Omit<Charge, 'id'>>;
  /** Payment identifier and only the mutated properties */
  payment?: Partial<Pick<Payment, 'id'>>;
  /** When true, forces creation of a new driver payment instead of updating an existing one */
  asCopy?: boolean;
}

export interface ChargeResponseRaw {
  isAgentPayNegative: boolean;
  /** Only returned by addCharge — flag indicating the user can approve the consignment */
  canApprove?: boolean;
  /** Only returned by addCharge — flag indicating the consignment has at least one unapproved charge */
  hasUnapprovedChg?: boolean;
  charges: ChargeRaw[];
  payments: PaymentRaw[];
}

export interface ChargeResponse {
  charges: Charge[];
  payments: Payment[];
}

export interface DeleteChargeResponse extends ChargeResponseRaw {
  alert?: string;
}

interface BasePaymentRequest extends BaseApiRequest {
  csnId: number;
}

export interface PostPaymentRequest extends BasePaymentRequest {
  /** Only the mutated properties of the Payment without any id because it is a new entry */
  payment: Partial<Omit<Payment, 'id'>>;
}

export interface PatchPaymentRequest extends BasePaymentRequest {
  /** Payment identifier and only the mutated properties */
  payment: Partial<Pick<Payment, 'id'>>;
  optimistic?: boolean;
}

export type GetRatePutRequest = BasePutApiRequest<
  'getRate',
  {
    csnId: number;
    drvId: string;
    svcId: string;
    date: string;
  }
>;

export type AddNotePutRequest = BasePutApiRequest<
  'addNote',
  {
    paymentId: number;
    note: string;
    type: 'Public' | 'Private';
    csnId: number;
  }
>;

export type ReleasePaymentApiRequest = BasePutApiRequest<'release' | 'unrelease', { paymentIds: number[] }> & {
  consignmentId: number;
};

export type DeletePaymentsApiRequest = BasePutApiRequest<'delete', { paymentIds: number[] }> & {
  consignmentId: number;
};

interface PutPaymentGetRateResponse {
  id: number;
  chgCodeId: string;
  amt: number;
}

export type PutPaymentResponse = PutPaymentGetRateResponse;

/**
 * Shared function to update the Charges and Payments endpoint caches with the response collections
 * @param consignmentId Identifer of the Consignment being mutated
 * @param param1 Charges & Payments collections returned from the API
 */
export async function updateChargesAndPaymentsCache(
  consignmentId: number,
  { charges, payments, isAgentPayNegative, canApprove, hasUnapprovedChg }: ChargeResponseRaw
) {
  const terminalNumber = store.getState().applicationState.selectedTerminal?.number;
  if (!terminalNumber) throw new Error('Terminal Number is required to update the API cache');

  store.dispatch(
    consignmentSlice.util.updateQueryData('fetchConsignment', { consignmentId: consignmentId, terminalNumber: terminalNumber }, (draft) => {
      Object.assign(draft, {
        isAgentPayNegative: isAgentPayNegative || false,
        // Only patch approval flags when the API returned them (addCharge does, patch/delete/post payment do not)
        ...(canApprove !== undefined && { canApprove }),
        ...(hasUnapprovedChg !== undefined && { hasUnapprovedChg })
      });
    })
  );

  await store.dispatch(
    chargesAndPaymentsSlice.util.upsertQueryData(
      'fetchCharges',
      {
        terminalNumber: terminalNumber,
        csnId: consignmentId
      },
      transformChargeResponse([...charges])
    )
  );

  await store.dispatch(
    chargesAndPaymentsSlice.util.upsertQueryData(
      'fetchPayments',
      {
        terminalNumber: terminalNumber,
        csnId: consignmentId
      },
      transformPaymentResponse([...payments])
    )
  );
}
